<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useI18n } from 'vue-i18n';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';

import Banner from 'dashboard/components-next/banner/Banner.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
});

// The dispatch job that sends these messages runs every minute (see
// config/schedule.yml), so a message can leave "pending" at any moment
// without this component knowing — poll instead of fetching only on mount,
// otherwise the banner keeps showing an already-sent message as pending
// until the conversation is reopened.
const REFRESH_INTERVAL_MS = 20000;

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { updateUISettings } = useUISettings();
const { dateTime } = useLocalizedTime();

const fetchScheduledMessages = () => store.dispatch('scheduledMessages/get');

let refreshInterval = null;

onMounted(() => {
  fetchScheduledMessages();
  refreshInterval = setInterval(fetchScheduledMessages, REFRESH_INTERVAL_MS);
});

onUnmounted(() => {
  clearInterval(refreshInterval);
});

const pendingScheduledMessages = computed(() =>
  getters['scheduledMessages/getScheduledMessagesByConversationId']
    .value(props.conversationId)
    .filter(record => record.status === 'pending')
    .sort((a1, a2) => a1.scheduled_at - a2.scheduled_at)
);

const nextScheduledMessage = computed(() => pendingScheduledMessages.value[0]);

const bannerMessage = computed(() => {
  const message = nextScheduledMessage.value;
  if (!message) return '';

  const date = dateTime(message.scheduled_at);
  const count = pendingScheduledMessages.value.length;

  return count === 1
    ? t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULED_BANNER_SINGLE', { date })
    : t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULED_BANNER_MULTIPLE', {
        count,
        date,
      });
});

const openActiveSchedules = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
    is_scheduled_messages_open: true,
  });
};
</script>

<template>
  <Banner
    v-if="nextScheduledMessage"
    color="blue"
    class="mx-2 mb-2 rounded-lg"
    :action-label="$t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULED_BANNER_ACTION')"
    @action="openActiveSchedules"
  >
    {{ bannerMessage }}
  </Banner>
</template>
